import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { createAppTheme } from '../theme/createAppTheme';
import {
  applyThemeCssVariables,
  DEFAULT_THEME_ID,
  getStoredThemeId,
  getThemePresetById,
  setStoredThemeId,
} from '../theme/themePresets';

const AppThemeContext = createContext(null);

export const AppThemeProvider = ({ children }) => {
  const [themeId, setThemeIdState] = useState(() => getStoredThemeId());

  const preset = useMemo(() => getThemePresetById(themeId), [themeId]);
  const muiTheme = useMemo(() => createAppTheme(preset), [preset]);

  useEffect(() => {
    applyThemeCssVariables(preset);
  }, [preset]);

  const setThemeId = useCallback((id) => {
    const next = getThemePresetById(id);
    if (!next) return;
    setThemeIdState(next.id);
    setStoredThemeId(next.id);
  }, []);

  const value = useMemo(
    () => ({
      themeId,
      preset,
      setThemeId,
    }),
    [themeId, preset, setThemeId]
  );

  return (
    <AppThemeContext.Provider value={value}>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </AppThemeContext.Provider>
  );
};

export const useAppTheme = () => {
  const ctx = useContext(AppThemeContext);
  if (!ctx) {
    throw new Error('useAppTheme must be used within AppThemeProvider');
  }
  return ctx;
};

/** Safe hook for optional usage outside provider (returns default preset) */
export const useAppThemeOptional = () => {
  const ctx = useContext(AppThemeContext);
  if (!ctx) {
    return {
      themeId: DEFAULT_THEME_ID,
      preset: getThemePresetById(DEFAULT_THEME_ID),
      setThemeId: () => {},
    };
  }
  return ctx;
};
